import type { CombinedError } from "urql";
import type { ResultOf } from "@/data-access/graphql";
import type {
  CreateTemplateLayout,
  GetTemplateLayout,
  ListTemplateLayouts,
  UpdateTemplateLayout,
} from "@/data-access/scheduled-briefings/queries/template-layout-queries";
import { validateTemplateLayout } from "@/lib/template-layout/validateTemplateLayout";
import type {
  TemplateLayoutListResult,
  TemplateLayoutRepositoryError,
  TemplateLayoutRepositoryResult,
} from "@/lib/template-studio/repository/templateLayoutRepository.types";
import type {
  TemplateLayout,
  TemplateLayoutScope,
  TemplateLayoutStatus,
  TemplateLayoutSummary,
  TemplateLayoutValidationError,
} from "@/types/template-layout/templateLayout.types";

type ListTemplateLayoutsData = ResultOf<typeof ListTemplateLayouts>;
type TemplateLayoutSummaryNode =
  ListTemplateLayoutsData["templateLayouts"]["edges"][number]["node"];
type GetTemplateLayoutData = ResultOf<typeof GetTemplateLayout>;
type TemplateLayoutNode =
  GetTemplateLayoutData["templateLayouts"]["edges"][number]["node"];
type TemplateLayoutMutationPayload =
  | ResultOf<typeof CreateTemplateLayout>["createTemplateLayout"]
  | ResultOf<typeof UpdateTemplateLayout>["updateTemplateLayout"];

const requestFailure = (message: string): TemplateLayoutRepositoryError => ({
  code: "REQUEST_FAILURE",
  message,
});

const invalidDocument = (
  message: string,
  validationErrors: TemplateLayoutValidationError[],
): TemplateLayoutRepositoryError => ({
  code: "INVALID_DOCUMENT",
  message,
  validationErrors,
});

const normalizeScope = (scope: string): TemplateLayoutScope | null => {
  const normalized = scope.toLowerCase();
  return normalized === "global" || normalized === "tenant" ? normalized : null;
};

const normalizeStatus = (status: string): TemplateLayoutStatus | null => {
  const normalized = status.toLowerCase();
  return normalized === "draft" ||
    normalized === "published" ||
    normalized === "archived"
    ? normalized
    : null;
};

const mapSummary = (
  node: TemplateLayoutSummaryNode,
): TemplateLayoutRepositoryResult<TemplateLayoutSummary> => {
  const scope = normalizeScope(node.scope);
  const status = normalizeStatus(node.status);
  if (scope === null || status === null) {
    return {
      success: false,
      error: requestFailure("Template metadata returned an unsupported value."),
    };
  }

  return {
    success: true,
    data: {
      id: node.id,
      name: node.name,
      ...(node.description ? { description: node.description } : {}),
      scope,
      status,
      canEdit: node.canEdit,
      slotCount: node.slotCount,
      briefingCount: node.briefingCount,
      createdBy: node.createdBy,
      createdAt: node.createdAt,
      updatedBy: node.updatedBy,
      updatedAt: node.updatedAt,
    },
  };
};

const mapLayout = (
  node: TemplateLayoutNode,
): TemplateLayoutRepositoryResult<TemplateLayout> => {
  const summary = mapSummary(node);
  if (!summary.success) return summary;

  const document = validateTemplateLayout(node.document);
  if (!document.success) {
    return {
      success: false,
      error: invalidDocument("Template document is invalid.", document.errors),
    };
  }

  return {
    success: true,
    data: {
      ...summary.data,
      document: document.data,
    },
  };
};

export const mapListTemplateLayoutsResponse = (
  data: ListTemplateLayoutsData,
): TemplateLayoutRepositoryResult<TemplateLayoutListResult> => {
  const items: TemplateLayoutSummary[] = [];

  for (const edge of data.templateLayouts.edges) {
    const summary = mapSummary(edge.node);
    if (summary.success) items.push(summary.data);
  }

  return {
    success: true,
    data: {
      items,
      pageInfo: {
        hasNextPage: data.templateLayouts.pageInfo.hasNextPage,
        endCursor: data.templateLayouts.pageInfo.endCursor ?? null,
      },
    },
  };
};

export const mapGetTemplateLayoutResponse = (
  data: GetTemplateLayoutData,
): TemplateLayoutRepositoryResult<TemplateLayout> => {
  const node = data.templateLayouts.edges[0]?.node;
  if (node === undefined) {
    return {
      success: false,
      error: { code: "NOT_FOUND", message: "Template not found." },
    };
  }

  return mapLayout(node);
};

const mapGraphQLErrorCode = (
  code: unknown,
  message: string,
): TemplateLayoutRepositoryError | null => {
  switch (code) {
    case "STALE_TEMPLATE":
      return { code: "STALE_TEMPLATE", message };
    case "TEMPLATE_IN_USE":
      return { code: "TEMPLATE_IN_USE", message };
    case "NOT_OWNER":
      return { code: "NOT_OWNER", message };
    case "NOT_FOUND":
      return { code: "NOT_FOUND", message };
    case "INVALID_ASSET":
    case "INVALID_DOCUMENT":
      return invalidDocument(message, [{ message }]);
    default:
      return null;
  }
};

export const mapOperationInfo = (
  payload: Extract<
    TemplateLayoutMutationPayload,
    { __typename: "OperationInfo" }
  >,
): TemplateLayoutRepositoryError => {
  const message =
    payload.messages.map((entry) => entry.message).join(" ") ||
    "Template operation failed.";

  for (const entry of payload.messages) {
    const mapped = mapGraphQLErrorCode(entry.code, message);
    if (mapped !== null) return mapped;
  }

  const permissionMessage = payload.messages.find(
    (entry) => entry.kind === "PERMISSION",
  );
  if (permissionMessage !== undefined) {
    return { code: "NOT_OWNER", message };
  }

  const validationMessages = payload.messages.filter(
    (entry) => entry.kind === "VALIDATION",
  );
  if (validationMessages.length === 0) return requestFailure(message);

  return invalidDocument(
    message,
    validationMessages.map((entry) => ({
      ...(entry.field ? { path: entry.field } : {}),
      message: entry.message,
    })),
  );
};

export const mapTemplateLayoutMutationResponse = (
  payload: TemplateLayoutMutationPayload,
): TemplateLayoutRepositoryResult<TemplateLayout> => {
  if (payload.__typename === "OperationInfo") {
    return { success: false, error: mapOperationInfo(payload) };
  }

  return mapLayout(payload);
};

export const mapTemplateLayoutGraphQLError = (
  error: Pick<CombinedError, "graphQLErrors" | "networkError">,
): TemplateLayoutRepositoryError => {
  for (const graphQLError of error.graphQLErrors) {
    const mapped = mapGraphQLErrorCode(
      graphQLError.extensions?.code,
      graphQLError.message,
    );
    if (mapped !== null) return mapped;
  }

  return requestFailure(
    error.networkError?.message ||
      error.graphQLErrors[0]?.message ||
      "Template request failed.",
  );
};
